import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { KeyRound, Pencil, Plus } from "lucide-react";
import { dashboardApi } from "@/api/auth";
import { Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { DashboardEngine } from "@/dashboard/DashboardEngine";
import { PersonalBand } from "@/dashboard/PersonalBand";
import { useDashboardRole } from "@/dashboard/DashboardRoleProvider";

/**
 * The one adaptive staff dashboard. The active role (primary by default, or
 * the header "Viewing as" choice for multi-hat users) selects a server-side
 * recipe; the engine renders it next to the personal "what needs me right
 * now" band that every role gets. Replaces the old role cascade and the
 * hidden /dashboard/* pages.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const { activeRole, roleTitle } = useDashboardRole();

  const dashboard = useQuery({
    queryKey: ["dashboard", activeRole],
    queryFn: () => dashboardApi.get(activeRole ?? undefined),
    enabled: !!user,
  });

  const resolvedRole = dashboard.data?.roleCode || activeRole || "";
  const eyebrow = resolvedRole ? `Role · ${resolvedRole} · ${roleTitle(resolvedRole)}` : "Overview";

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            {eyebrow}
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            Welcome, {user?.displayName ?? ""}.
          </h1>
        </div>
        <div className="flex items-center gap-space-2">
          <Link to="/account/profile/edit">
            <Button variant="primary">
              {user?.profileCompletedAt ? (
                <><Pencil size={14} strokeWidth={2} aria-hidden /> Edit Profile</>
              ) : (
                <><Plus size={14} strokeWidth={2} aria-hidden /> New Profile</>
              )}
            </Button>
          </Link>
          <Link to="/account/password">
            <Button variant="secondary">
              <KeyRound size={14} strokeWidth={2} aria-hidden /> Change Password
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-space-4 items-start">
        <DashboardEngine
          widgets={dashboard.data?.widgets}
          activeRole={resolvedRole}
          isLoading={dashboard.isLoading}
          isError={dashboard.isError}
        />
        <PersonalBand />
      </div>
    </div>
  );
}
